import {render} from './../../../reducers/rootReducer';
import {type Stripe, type PaymentRequest} from '@stripe/stripe-js';
import {type TokenResult, type ConfirmResult} from '../../../../../@type/stripe';
import {type GatewayConfiguration} from '../../../models/GatewayConfiguration';
import {type StripeGateway, type IStripeContext} from '../../../models/stripe';
import {DefaultCart} from '../../../reducers/cartReducer';
import {store} from '../../../store';
import {getLocaleText} from '../../../util/translation';
import {createPaymentRequestOptions, tokenizePaymentRequest, updatePaymentRequestOptions} from '../payment-request';
import {createSetupIntent} from '../setup-intent';
import applepayBadgeURL from '../../../../img/badge/apple-pay.svg';
import applepayImageURL from '../../../../img/marks/apple-pay.svg';
import {Feature} from '../../../reducers/environmentReducer';
import {FeatureFlag} from '../../../../../@type/features';

function initStripeApplepayMethod(): StripeGateway {
	let paymentRequest: PaymentRequest | null = null;

	const config: GatewayConfiguration = {
		name: Feature.metadata<string>(FeatureFlag.STRIPE_APPLEPAY_GATEWAY, 'title') ?? getLocaleText('Apple Pay'),
		gatewayId: 'peachpay_stripe_applepay',
		description: getDescription(),
		assets: {
			title: {src: applepayImageURL},
			badge: {src: applepayBadgeURL},
		},
	};

	store.subscribe(() => {
		paymentRequest?.update(updatePaymentRequestOptions());
	});

	const init = async (stripe: Stripe) => {
		try {
			paymentRequest = stripe.paymentRequest(createPaymentRequestOptions());

			const result = await paymentRequest.canMakePayment();

			if (!result?.['applePay']) {
				config.initialized = false;
				store.dispatch(render());
				return;
			}
		} catch (error) {
			console.error(error);
		}
	};

	return {
		config,
		init(context: IStripeContext) {
			const {stripe} = context;

			let initialized = false;
			store.subscribe(() => {
				if (DefaultCart.contents().length > 0 && !initialized) {
					initialized = true;
					store.dispatch(render());
					init(stripe).catch(() => {/**/});
				}
			});
		},
		async tokenize(context: IStripeContext, sessionId: string): Promise<TokenResult> {
			const {stripe} = context;
			if (!paymentRequest) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Failed to tokenize payment method because paymentRequest object is not initialized.',
				}];
			}

			const localPaymentRequest = paymentRequest;
			paymentRequest = null;

			const [paymentMethodId, update] = await tokenizePaymentRequest(localPaymentRequest);
			if (!paymentMethodId) {
				await init(stripe);
				return [null, update];
			}

			// Check for a cart total of 0. If it's more than zero, we do not need setupIntent.
			// See https://www.notion.so/peachpay/Stripe-3D-Secure-card-payments-sometimes-fail-01fae4e0b8694e3680f8d6f6733770df.
			if (DefaultCart.total() > 0) {
				return [paymentMethodId, {}];
			}

			const clientSecret = await createSetupIntent(sessionId, 'card', paymentMethodId);
			if (!clientSecret) {
				await init(stripe);
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Failed to create setup intent.',
				}];
			}

			const {error: confirmError, setupIntent: confirmIntent} = await stripe.confirmCardSetup(clientSecret);
			if (confirmError ?? !confirmIntent) {
				await init(stripe);
				return [null, {
					paymentStatus: 'creationfailed',
					note: confirmError?.message ?? 'Failed to confirm setup intent.',
				}];
			}

			if (confirmIntent.status !== 'succeeded') {
				await init(stripe);
				return [null, {
					paymentStatus: 'creationfailed',
					note: `Setup intent resulted in invalid status(${confirmIntent.status})`,
				}];
			}

			await init(stripe);
			return [confirmIntent.payment_method as string, {}];
		},
		async confirm(context: IStripeContext, clientSecret: string): Promise<ConfirmResult> {
			const {stripe} = context;
			const {paymentIntent, error} = await stripe.retrievePaymentIntent(clientSecret);

			if (!paymentIntent || error) {
				const message = error?.message ?? 'Failed to retrieve payment intent.';
				return [null, {
					note: message,
				}];
			}

			if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
				return ['success', {}];
			}

			if (paymentIntent.status === 'requires_action') {
				const {paymentIntent, error} = await stripe.confirmCardPayment(clientSecret);
				if (!paymentIntent || error) {
					const message = error?.message ?? 'Failed to confirm payment intent.';
					return [null, {
						note: message,
					}];
				}

				if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
					return ['success', {}];
				}
			}

			return [null, {
				note: `Failed to confirm payment intent with status(${paymentIntent.status})`,
			}];
		},
	};
}

export {initStripeApplepayMethod};

function getDescription(): string {
	return /* html */ `
<img style="display: block; text-align: left; height: 1.5rem; " src="${applepayImageURL}">
<p style="text-align: left; margin: 0.5rem 0;">${getLocaleText('Apple Pay selected for checkout.')}</p>
<hr/>
<p style="text-align: left; margin: 0.5rem 0 0;" class="muted">${getLocaleText('Another step will appear after submitting your order to complete your purchase details.')}<p>
    `;
}
